import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createDocument,
  deleteDocument,
  getBulkPlanningDocuments,
  getBulkPlanningWithQuantity,
  getCivilBulkPlanningDocuments,
  getDocument,
  getDocumentWithProjectUsers,
  getDocuments,
  getDocumentsBulkPlanningMasterData,
  getDocumentsMasterData,
  getElectricalBulkPlanningDocuments,
  getPipingBulkPlanningDocuments,
  getPipingProjectDocuments,
  updateActiveDocument,
  updateDocument
} from '../../../api/documents';
import { ProjectDocument } from '../../ProjectDocument/projectDocumentSlice';
import { AimsDocumentType } from '../AimsDocumentType/aimsDocumentTypeSlice';
import { CivilJobList } from '../CivilJobList/civilJobListSlice';
import { Discipline } from '../Discipline/disciplineSlice';
import { DocumentGroup } from '../DocumentGroup/documentGroupSlice';
import { ProjectPosition } from '../ProjectPosition/projectPositionSlice';
import { WorkflowStep } from '../WorkflowStep/workflowStepSlice';

export const getDocumentsAsync = createAsyncThunk(
  'documents/getDocuments',
  getDocuments
);

export const getDocumentsBulkPlanningMasterDataAsync = createAsyncThunk(
  'documents/getDocumentsBulkPlanningMasterData',
  getDocumentsBulkPlanningMasterData
);

export const getDocumentsMasterDataAsync = createAsyncThunk(
  'documents/getDocumentsMasterData',
  getDocumentsMasterData
);

export const getBulkPlanningDocumentsAsync = createAsyncThunk(
  'documents/getBulkPlanningDocuments',
  getBulkPlanningDocuments
);

export const getBulkPlanningWithQuantityAsync = createAsyncThunk(
  'documents/getBulkPlanningWithQuantity',
  getBulkPlanningWithQuantity
);

export const getPipingBulkPlanningDocumentsAsync = createAsyncThunk(
  'documents/getPipingBulkPlanningDocuments',
  getPipingBulkPlanningDocuments
);

export const getPipingProjectDocumentsAsync = createAsyncThunk(
  'documents/getPipingProjectDocuments',
  getPipingProjectDocuments
);

export const getCivilBulkPlanningDocumentsAsync = createAsyncThunk(
  'documents/getCivilBulkPlanningDocuments',
  getCivilBulkPlanningDocuments
);

export const getElectricalBulkPlanningDocumentsAsync = createAsyncThunk(
  'documents/getElectricalBulkPlanningDocuments',
  getElectricalBulkPlanningDocuments
);

export const getDocumentAsync = createAsyncThunk(
  'documents/getDocument',
  getDocument
);

export const getDocumentWithProjectUsersAsync = createAsyncThunk(
  'documents/getDocumentWithProjectUsers',
  getDocumentWithProjectUsers
);

export const createDocumentAsync = createAsyncThunk(
  'documents/createDocument',
  createDocument
);

export const updateDocumentAsync = createAsyncThunk(
  'documents/updateDocument',
  updateDocument
);

export const updateActiveDocumentAsync = createAsyncThunk(
  'documents/updateActiveDocument',
  updateActiveDocument
);

export const deleteDocumentAsync = createAsyncThunk(
  'documents/deleteDocument',
  deleteDocument
);

export interface Document {
  id: number;
  name: string;
  no: string;
  disciplineId: number;
  discipline: Discipline;
  aimsDocumentTypeId: number | null;
  aimsDocumentType: AimsDocumentType | null;
  documentGroupId: number | null;
  documentGroup: DocumentGroup | null;
  isActive: boolean;
}

interface ProjectPositionWithUser extends ProjectPosition {
  userProjectPositions: { userId: number }[];
}

export interface DocumentWithProjectUser extends Document {
  projectPositionWorkflowSteps: {
    id: number;
    projectPositionId: number;
    projectPosition: ProjectPositionWithUser | null; // return `null` when cannot find user in `UserProjectPosition` table
    workflowStepId: number;
    workflowStep: WorkflowStep | null; // return `null` when cannot find workflow step in `WorkflowStep` table
  }[]; // return `[]` (blank array) if cannot find standard workflow
}

interface DocumentWithProjectDocument extends Document {
  projectDocuments: ProjectDocument[];
}

export interface CivilTemplateDocument {
  id: number;
  no: string;
  name: string;
  documentGroup: DocumentGroup | null;
  civilJobLists: Omit<CivilJobList, 'defaultQuantity'>[];
  defaultQuantity: number;
}

export interface ElectricalTemplateDocument {
  id: number;
  no: string;
  name: string;
  documentGroup: DocumentGroup | null;
  electricalLists: { id: number; name: string }[];
}

interface DocumentState {
  data: Document[];
  bulkPlanningMasterData: Document[];
  bulkPlanning: DocumentWithProjectUser[];
  pipingTemplateDocuments: Document[];
  pipingProjectDocuments: DocumentWithProjectDocument[];
  civilTemplateDocuments: CivilTemplateDocument[];
  electricalTemplateDocuments: ElectricalTemplateDocument[];
  masterData: Document[];
  count: number;
  selectedDocument: Document | null;
  documentWithProjectUsers: DocumentWithProjectUser | null;
}

const initialState: DocumentState = {
  data: [],
  bulkPlanningMasterData: [],
  bulkPlanning: [],
  pipingTemplateDocuments: [],
  pipingProjectDocuments: [],
  civilTemplateDocuments: [],
  electricalTemplateDocuments: [],
  masterData: [],
  count: 0,
  selectedDocument: null,
  documentWithProjectUsers: null
};

export default createSlice({
  name: 'documents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getDocumentsAsync.fulfilled, (state, action) => {
      state.data = action.payload.data;
    });
    builder.addCase(
      getDocumentsBulkPlanningMasterDataAsync.fulfilled,
      (state, action) => {
        state.bulkPlanningMasterData = action.payload.data;
      }
    );
    builder.addCase(
      getBulkPlanningDocumentsAsync.fulfilled,
      (state, action) => {
        state.bulkPlanning = action.payload.data;
      }
    );
    builder.addCase(
      getBulkPlanningWithQuantityAsync.fulfilled,
      (state, action) => {
        state.bulkPlanning = action.payload.data;
      }
    );
    builder.addCase(
      getPipingBulkPlanningDocumentsAsync.fulfilled,
      (state, action) => {
        state.pipingTemplateDocuments = action.payload.data;
      }
    );
    builder.addCase(
      getPipingProjectDocumentsAsync.fulfilled,
      (state, action) => {
        state.pipingProjectDocuments = action.payload.data;
      }
    );
    builder.addCase(
      getCivilBulkPlanningDocumentsAsync.fulfilled,
      (state, action) => {
        state.civilTemplateDocuments = action.payload.data;
      }
    );
    builder.addCase(
      getElectricalBulkPlanningDocumentsAsync.fulfilled,
      (state, action) => {
        state.electricalTemplateDocuments = action.payload.data;
      }
    );
    builder.addCase(getDocumentsMasterDataAsync.fulfilled, (state, action) => {
      state.masterData = action.payload.data;
      state.count = action.payload.count;
    });
    builder.addCase(getDocumentAsync.fulfilled, (state, action) => {
      state.selectedDocument = action.payload;
    });
    builder.addCase(
      getDocumentWithProjectUsersAsync.fulfilled,
      (state, action) => {
        state.documentWithProjectUsers = action.payload;
      }
    );
    builder.addCase(updateActiveDocumentAsync.fulfilled, (state, action) => {
      const updatedDocument = action.payload;
      const indexOfDocument =
        state.masterData.findIndex(
          (document) => document.id === updatedDocument.id
        ) ?? -1;
      if (indexOfDocument >= 0) {
        const newDocument = {
          ...state.masterData[indexOfDocument],
          isActive: updatedDocument.isActive
        };
        const newDocuments = [
          ...state.masterData.slice(0, indexOfDocument),
          newDocument,
          ...state.masterData.slice(indexOfDocument + 1)
        ];
        state.masterData = newDocuments;
      }
    });
  }
});
